import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { useParams } from 'react-router-dom';

import { ArticleLinksSection } from '@/components/ArticleLinksSection/ArticleLinksSection';
import { ArticleRelatedItemsSection } from '@/components/ArticleRelatedItemsSection/ArticleRelatedItemsSection';
import { MarkdownContent } from '@/components/MarkdownContent/MarkdownContent';
import NotFound from '@/pages/NotFound/NotFound';
import { useAppSelector } from '@/redux/hooks';
import {
    getArticle,
    getArticleError,
    getArticleLoaded,
    getArticleLoading,
} from '@/redux/modules/article/article.selectors';
import { getArticleLinks } from '@/redux/modules/articleLinks/articleLinks.selectors';
import { getArticleRelatedItemIds } from '@/redux/modules/articleRelatedItems/articleRelatedItems.selectors';
import { getItemSummaries } from '@/redux/modules/item/summary/itemSummary.selectors';

import { ArticlePageHeaderMeta } from './ArticlePageHeaderMeta';
import { ArticlePageTitleBlock } from './ArticlePageTitleBlock';

const ArticlePage = () => {
    const { slug, topic } = useParams<{ slug: string; topic: string }>();
    const article = useAppSelector(getArticle);
    const error = useAppSelector(getArticleError);
    const isLoaded = useAppSelector(getArticleLoaded);
    const isLoading = useAppSelector(getArticleLoading);
    const relatedItemIds = useAppSelector(getArticleRelatedItemIds);
    const relatedItemSummaries = useAppSelector((state) => getItemSummaries(state, relatedItemIds));
    const relatedArticles = useAppSelector(getArticleLinks);
    const hasRelatedContent = relatedItemSummaries.length > 0 || relatedArticles.length > 0;

    if (error) {
        return (
            <div
                className="mx-auto w-full max-w-5xl px-sm py-lg"
                data-testid="article-page-error"
            >
                <BodyPrimary>Unable to load this article. Please try again later.</BodyPrimary>
            </div>
        );
    }

    if (isLoading || !isLoaded) {
        return (
            <div
                className="flex justify-center py-2xl"
                data-testid="article-page-loading"
            >
                <Loading />
            </div>
        );
    }

    // Clean miss after a successful fetch is a soft-404
    if (!article) {
        return <NotFound />;
    }

    return (
        <div className="mx-auto flex max-w-[1644px] flex-col items-center gap-xl border-t-0 border-b border-border-primary px-sm pb-xl smMin:gap-2xl smMin:px-2xl smMin:pb-2xl">
            <ArticlePageHeaderMeta />
            <article
                className="mx-auto w-full max-w-5xl px-sm pt-md smMin:pt-xl"
                data-testid="article-page"
                data-article-slug={slug}
                data-article-topic={topic}
                id="article"
            >
                <div className="mx-auto w-full max-w-[48rem]">
                    <ArticlePageTitleBlock
                        author={article.author}
                        createdAt={article.createdAt}
                        imageUrl={article.heroImageUrl}
                        subtitle={article.subtitle}
                        title={article.title}
                    />
                    <MarkdownContent content={article.content} />
                </div>
            </article>
            {hasRelatedContent && <hr className="w-11/12 border-t border-b-0 border-border-primary" />}
            <ArticleRelatedItemsSection className="w-full" />
            <ArticleLinksSection className="w-full" />
        </div>
    );
};

export default ArticlePage;
